import React, { useState } from "react";

import { Info } from "lucide-react";

import DeliveryInfo from "~/components/delivery/DeliveryInfo";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import {
  getStatusLabel,
  getStatusTextColor,
} from "~/pages/purchase-orders/utils/statusLabels";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";

interface Transportista {
  id: string;
  name: string;
  lastname: string;
}

interface SalesCondition {
  id: string;
  name: string;
  maximumDays?: number;
}

interface OrderSummaryCardProps {
  selectedOrder: SalesRequest;
  clientName: string;
  clientTaxId: string | null;
  seller: { name: string; lastname: string };
  canChangeSeller?: boolean;
  selectedSellerId?: string;
  handleSellerChange?: (sellerId: string) => void;
  isSubmitting?: boolean;
  sellers?: { id: string; name: string; lastname: string }[];
  userRole: string;
  transportistas: Transportista[];
  assignedTransportista: Transportista | null;
  handleTransportistaChange: (transportistaId: string) => void;
  isAssigningTransportista: boolean;
  isLoadingTransportistas: boolean;
  clientInfo?: Record<string, ClientInfo>;
  clientInternalNotes: string | null;
  effectiveSalesCondition: SalesCondition | undefined;
  clientSalesConditionId: string | null;
  canModifySettings: boolean;
  salesConditions: SalesCondition[];
  isEditingSalesCondition: boolean;
  setIsEditingSalesCondition: (v: boolean) => void;
  selectedSalesConditionId: string | undefined;
  setSelectedSalesConditionId: (id: string | undefined) => void;
  savingSalesCondition: boolean;
  onSaveSalesCondition: () => Promise<void>;
}

// The summary card keeps its own established wording for these statuses
// (shorter than the shared workflow-oriented labels); anything else falls
// back to the shared map so raw slugs never render.
const SUMMARY_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  prepaid: "Esperando Pago",
  delivered: "Despachada",
};

const getSummaryStatusLabel = (status: string): string =>
  SUMMARY_STATUS_LABELS[status] ?? getStatusLabel(status);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </p>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-medium text-gray-500">{children}</p>
);

export const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  selectedOrder,
  clientName,
  clientTaxId,
  seller,
  canChangeSeller = false,
  selectedSellerId,
  handleSellerChange = () => {},
  isSubmitting = false,
  sellers = [],
  userRole,
  transportistas,
  assignedTransportista,
  handleTransportistaChange,
  isAssigningTransportista,
  isLoadingTransportistas,
  clientInfo,
  clientInternalNotes,
  effectiveSalesCondition,
  clientSalesConditionId,
  canModifySettings,
  salesConditions,
  isEditingSalesCondition,
  setIsEditingSalesCondition,
  selectedSalesConditionId,
  setSelectedSalesConditionId,
  savingSalesCondition,
  onSaveSalesCondition,
}) => {
  const [isRutCopied, setIsRutCopied] = useState(false);

  const copyRutToClipboard = () => {
    if (clientTaxId) {
      navigator.clipboard.writeText(clientTaxId);
      setIsRutCopied(true);
      setTimeout(() => setIsRutCopied(false), 2000);
    }
  };

  const canManageTransportistas =
    userRole === "superadmin" || userRole === "logistica";

  const fiscalAddress = clientInfo?.[selectedOrder.clientId]?.fiscalAddress;

  return (
    <div className="overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm">
      <div className="border-b border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-blue-900">
          <Info className="mr-2 h-5 w-5 text-blue-600" />
          Información de la Nota
        </h3>
      </div>

      <div className="space-y-4 p-4">
        {/* Cliente */}
        <section className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
          <SectionTitle>Cliente</SectionTitle>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <FieldLabel>Razón Social</FieldLabel>
              <p className="text-sm font-semibold">{clientName}</p>
              {clientTaxId && (
                <button
                  onClick={copyRutToClipboard}
                  className="flex w-fit items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 transition-colors duration-200 hover:bg-gray-200/70 hover:text-gray-800"
                  title="Click para copiar RUT"
                >
                  <span>RUT: {clientTaxId}</span>
                  {isRutCopied ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="opacity-60">📋</span>
                  )}
                </button>
              )}
            </div>

            <div className="space-y-1">
              <FieldLabel>Vendedor</FieldLabel>
              {canChangeSeller ? (
                <div className="space-y-2">
                  <Select
                    value={selectedSellerId}
                    onValueChange={handleSellerChange}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Seleccionar vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {sellers.map((sellerOption) => (
                        <SelectItem
                          key={sellerOption.id}
                          value={sellerOption.id}
                        >
                          {`${sellerOption.name} ${sellerOption.lastname}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Solo usuarios super admin pueden cambiar el vendedor
                  </p>
                </div>
              ) : (
                <p className="text-sm font-semibold">{`${seller.name} ${seller.lastname}`}</p>
              )}
            </div>
          </div>
        </section>

        {/* Detalles */}
        <section className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
          <SectionTitle>Detalles</SectionTitle>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <FieldLabel>Estado</FieldLabel>
              <p
                className={`text-sm font-semibold ${getStatusTextColor(selectedOrder.status)}`}
              >
                {getSummaryStatusLabel(selectedOrder.status)}
              </p>
            </div>

            <div className="space-y-1">
              <FieldLabel>Estado de Pago</FieldLabel>
              <p
                className={`text-sm font-semibold ${selectedOrder.paid ? "text-green-600" : "text-amber-600"}`}
              >
                {selectedOrder.paid ? "Pagado" : "Pendiente"}
              </p>
            </div>

            <div className="space-y-1">
              <FieldLabel>Fecha</FieldLabel>
              <p className="text-sm font-semibold">
                {new Date(selectedOrder.createdAt).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
            </div>

            <div className="space-y-1">
              <FieldLabel>Monto Total</FieldLabel>
              <p className="text-sm font-semibold">
                {formatCurrency(
                  grossFromNet(selectedOrder.totalAmount, selectedOrder.isExento)
                )}
                <span className="ml-1 text-xs text-gray-500">
                  {selectedOrder.isExento ? "(exento)" : "(con IVA)"}
                </span>
              </p>
              <p className="text-xs text-gray-500">
                Neto: {formatCurrency(selectedOrder.totalAmount)}
              </p>
            </div>

            {/* Sales Condition */}
            {userRole !== "transportista" && (
              <div
                className={`space-y-1 ${isEditingSalesCondition ? "col-span-2 md:col-span-3" : ""}`}
              >
                <FieldLabel>Condición de Venta</FieldLabel>
                {!isEditingSalesCondition ? (
                  <>
                    <p className="text-sm font-semibold">
                      {effectiveSalesCondition?.name ?? "Sin condición"}
                    </p>
                    {effectiveSalesCondition && (
                      <p className="text-xs text-muted-foreground">
                        {!selectedOrder.salesConditionId &&
                        clientSalesConditionId
                          ? "Heredada del cliente"
                          : "Asignada a esta nota"}
                      </p>
                    )}
                    {canModifySettings && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setIsEditingSalesCondition(true)}
                      >
                        Cambiar
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="max-w-md space-y-2">
                    <Select
                      value={
                        selectedSalesConditionId === undefined
                          ? "inherit"
                          : selectedSalesConditionId
                      }
                      onValueChange={setSelectedSalesConditionId}
                      disabled={savingSalesCondition}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Seleccionar condición" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">
                          Usar condición del cliente
                        </SelectItem>
                        {salesConditions.map((condition) => (
                          <SelectItem key={condition.id} value={condition.id}>
                            {condition.name} - {condition.maximumDays} días
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setIsEditingSalesCondition(false)}
                        disabled={savingSalesCondition}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={onSaveSalesCondition}
                        disabled={savingSalesCondition}
                      >
                        {savingSalesCondition ? "Guardando..." : "Guardar"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Entrega */}
        <section className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
          <SectionTitle>Entrega</SectionTitle>
          <div className="space-y-4">
            {canManageTransportistas && (
              <div className="space-y-1">
                <FieldLabel>Transportista</FieldLabel>
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Select
                      value={assignedTransportista?.id || ""}
                      onValueChange={handleTransportistaChange}
                      disabled={
                        isAssigningTransportista || isLoadingTransportistas
                      }
                    >
                      <SelectTrigger className="h-8 flex-1 text-xs">
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent>
                        {transportistas.map((transportista) => (
                          <SelectItem
                            key={transportista.id}
                            value={transportista.id}
                          >
                            {`${transportista.name} ${transportista.lastname}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {assignedTransportista && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => handleTransportistaChange("")}
                        disabled={
                          isAssigningTransportista || isLoadingTransportistas
                        }
                        title="Desasignar transportista"
                      >
                        ×
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isLoadingTransportistas
                      ? "Cargando transportistas..."
                      : isAssigningTransportista
                        ? "Procesando..."
                        : "Solo usuarios super admins y logística pueden cambiar el transportista"}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel>Dirección de Entrega</FieldLabel>
                <DeliveryInfo salesRequest={selectedOrder} variant="full" />
              </div>

              {fiscalAddress && (
                <div className="space-y-1">
                  <FieldLabel>Dirección Fiscal</FieldLabel>
                  <div className="text-sm">
                    <p className="font-semibold">
                      {fiscalAddress.street} {fiscalAddress.number}
                    </p>
                    <p className="text-xs text-gray-600">
                      {fiscalAddress.city}
                      {fiscalAddress.comuna && `, ${fiscalAddress.comuna}`}
                      {fiscalAddress.state && `, ${fiscalAddress.state}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Notas del Cliente */}
        {userRole !== "transportista" && clientInternalNotes && (
          <section className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
            <SectionTitle>Notas del Cliente</SectionTitle>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="whitespace-pre-wrap text-sm text-gray-700">
                {clientInternalNotes}
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
